import { AnswerOptionStatus } from '../constants/AnswerOptionStatus';
import meta from '../../data/meta.json';

export class Quiz {
    // Store links to the static configuration data
    #map;
    #comments;
    #config;
    #numQuestions;
    #questionsIds;
    #questionsOptionIds;
    #optionsMap;

    // State
    #questionsQueue;
    #totalScore;
    #numAnswers;
    #answers;
    #answersMap;
    #prevAnswer;
    #currentAnswer;
    #complete;
    #error;
    #errorMessage;

    constructor(config) {
        const { map, comments, questions } = config;

        this.#map = map;
        this.#comments = comments;
        this.#numQuestions = questions.length;
        this.#questionsIds = questions.map((q) => q.id);
        this.#questionsOptionIds = new Map(questions.map((q) => [q.id, q.options.map((op) => op.id)]));
        this.#optionsMap = new Map(questions.reduce((accumulator, q) => accumulator.concat(q.options.map((op) => [op.id, op])), []));

        this.#config = config;
    }

    reset() {
        const { questions, startWithScore } = this.#config;

        this.#questionsQueue = [...questions];
        this.#totalScore = startWithScore;
        this.#numAnswers = 0;
        this.#answers = [];
        this.#answersMap = new Map();
        this.#complete = false;
        this.#prevAnswer = null;
        this.#currentAnswer = null;
        this.#error = false;
        this.#errorMessage = null;
    }

    answer(questionId, value) {
        /* The Quiz is complete */
        if (this.#questionsQueue.length < 1) {
            this.#complete = true;
        }

        if (this.#complete) {
            this.#error = true;
            this.#errorMessage = 'There\'s no questions left';

            return false;
        }

        const currentQuestion = this.#questionsQueue[0];

        /* The wrong Question */
        if (questionId !== currentQuestion.id) {
            this.#error = true;
            this.#errorMessage = 'The wrong Question ID';

            return false;
        }

        /* The Question has been answered */
        if (this.#answersMap.get(questionId)) {
            this.#error = true;
            this.#errorMessage = `The Question with ID ${questionId} has been answered`;

            return false;
        }

        /* There's no such an answer for the current question */
        if (!value || !this.#questionsOptionIds.get(questionId).includes(value)) {
            this.#error = true;
            this.#errorMessage = `There's no answer with id ${value} for the current question (${questionId})`;

            return false;
        }

        /* There's no score data for this answer */
        if (typeof this.#map[value] !== 'number') {
            this.#error = true;
            this.#errorMessage = `There's no score data for the answer with id ${value}`;

            return false;
        }

        this.#prevAnswer = this.#currentAnswer;
        this.#currentAnswer = value;

        this.#answers.push(value);
        this.#answersMap.set(questionId, value);
        this.#numAnswers += 1;
        this.#totalScore += this.#map[value];

        return true;
    }

    nextQuestion() {
        // Take a next question
        this.#questionsQueue.shift();
        this.#prevAnswer = this.#currentAnswer;

        // There's no question lefts
        if (this.#questionsQueue.length < 1) {
            this.#complete = true;

            return;
        }
        this.#currentAnswer = null;
    }

    getResult() {
        if (!this.#answers || this.#answers.length < this.#numQuestions) return null;

        const { results } = this.#config;
        // Count score increments more then 90
        const { numCorrectAnswers } = this;

        let i;

        for (i = results.length - 1; i > -1; i -= 1) {
            if (results[i].max < numCorrectAnswers) break;
        }
        i += 1;
        if (i > results.length - 1) i = results.length - 1;

        let output = results[i];

        output = {
            ...output,
            meta: {
                ...output.meta,
                url: `${meta.url}${output.meta.path}`
            }
        };

        return output;
    }

    get currentQuestion() {
        if (!this.#questionsQueue || this.#questionsQueue.length < 1) return null;

        return this.#questionsQueue[0];
    }

    get currentQuestionIndex() {
        return this.#numQuestions - this.#questionsQueue.length;
    }

    get numQuestions() {
        return this.#numQuestions;
    }

    get numCorrectAnswers() {
        if (!this.#answers) return 0;

        return this.#answers.reduce((accumulate, value) => (accumulate + (this.#map[value] > 90 ? 1 : 0)), 0);
    }

    get totalScore() {
        return this.#totalScore;
    }

    get complete() {
        return this.#complete;
    }

    get error() {
        return this.#error;
    }

    get errorMessage() {
        return this.#errorMessage;
    }

    get isTheLastQuestion() {
        return this.#questionsQueue.length === 1;
    }

    get prevIncrement() {
        if (!this.#prevAnswer) return 0;

        return this.#map[this.#prevAnswer] || 0;
    }

    get currentAnswerStatus() {
        if (!this.#currentAnswer) return 0;

        const { options } = this.#questionsQueue[0];

        // const optionIds = this.#questionsOptionIds.get(currentQuestionId);

        return options
            .map(({ id, label }) => {
                let score = this.#map[id] || 0;

                // eslint-disable-next-line no-nested-ternary
                score = (score > 90 ? 1 : (score < -90 ? -1 : 0));

                let status = AnswerOptionStatus.UNDEFINED;

                if (score === 1) {
                    status = AnswerOptionStatus.CORRECT;
                } else if (id === this.#currentAnswer) {
                    status = score < 0 ? AnswerOptionStatus.INCORRECT : AnswerOptionStatus.OK;
                }

                return {
                    id, label, status, isAnswer: id === this.#currentAnswer
                };
            });
    }

    get currentIncrement() {
        if (!this.#currentAnswer) return 0;

        return this.#map[this.#currentAnswer] || 0;
    }

    get currentComment() {
        if (!this.#currentAnswer) return null;
        const comment = this.#comments[this.#currentAnswer] || null;
        const answer = this.#optionsMap.get(this.#currentAnswer);

        if (!answer || !comment) return null;

        return `${answer.label}\n\n${comment}`;
    }
}
